// Drop-in hook + helpers for every CatalystAgile PWA.
// Reads the Groq API key from localStorage (user-pasted at runtime), falling
// back to the build-time VITE_GROQ_API_KEY env var. Either path keeps the PWA
// fully functional for offline logic mode; the AI Enhance button is gated on
// hasKey() being true.

import { useEffect, useState } from 'react'

const STORAGE_KEY = 'ca_groq_key'

export function getGroqKey(): string {
  if (typeof window === 'undefined') return ''
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored && stored.trim()) return stored.trim()
  // Vite env fallback for devs running locally with a .env file.
  const env = (import.meta as any).env?.VITE_GROQ_API_KEY
  return typeof env === 'string' ? env : ''
}

export function setGroqKey(key: string): void {
  if (typeof window === 'undefined') return
  const trimmed = key.trim()
  if (!trimmed) {
    window.localStorage.removeItem(STORAGE_KEY)
  } else {
    window.localStorage.setItem(STORAGE_KEY, trimmed)
  }
  // Notify listeners in the same tab (storage event only fires cross-tab)
  window.dispatchEvent(new CustomEvent('ca-groq-key-changed'))
}

export function hasGroqKey(): boolean {
  return getGroqKey().length > 0
}

export function useGroqKey(): { key: string; hasKey: boolean; setKey: (k: string) => void } {
  const [key, setKey] = useState<string>(() => getGroqKey())

  useEffect(() => {
    const onChange = () => setKey(getGroqKey())
    window.addEventListener('storage', onChange)
    window.addEventListener('ca-groq-key-changed', onChange as EventListener)
    return () => {
      window.removeEventListener('storage', onChange)
      window.removeEventListener('ca-groq-key-changed', onChange as EventListener)
    }
  }, [])

  return {
    key,
    hasKey: key.length > 0,
    setKey: (k: string) => {
      setGroqKey(k)
      setKey(k)
    },
  }
}
